import { INF_SIZE_MAX_VALUE } from '../Persona/Utils/reconcileCreatorFiltersData';
import { SOCIAL_NETWORKS } from '../../constants/socialMedia';

// Playful "still generating" copy shared by the chat in-progress line and the
// list card progress bar, so both surfaces show the same messages.
export const DISCOVERY_LOADING_MESSAGES = [
  "Stalking profiles so you don't have to... 👀",
  'Scrolling through more feeds than your lunch break... 🍲',
  'Hunting unicorns... the influencer kind 🦄',
];

function hasSocialNetworkValue(record) {
  const sn = record?.socialNetwork;
  if (sn == null) return false;
  if (typeof sn === 'string' && sn.trim() === '') return false;
  return true;
}

/**
 * Accepts filterJson as either a plain object (new API) or a JSON string (legacy/created rows).
 */
export function parseFilterJson(filterJson) {
  if (filterJson == null) return null;
  if (typeof filterJson === 'object') return filterJson;
  if (typeof filterJson === 'string') {
    try {
      return JSON.parse(filterJson);
    } catch {
      return null;
    }
  }
  return null;
}

function platformsFromFilterJson(record) {
  if (!record?.filterJson) return [];
  const parsed = parseFilterJson(record.filterJson);
  if (parsed && Array.isArray(parsed.platforms) && parsed.platforms.length) {
    return parsed.platforms;
  }
  return [];
}

/** API list rows: `isLiked` is string `'1'` when liked, `'0'` when not. */
export function isDiscoveryListLiked(record) {
  return String(record?.isLiked) === '1';
}

/**
 * Custom lists are built from pasted profile URLs (Custom List mode sends
 * `isCustom=1`) rather than from an AI prompt. Tolerates 1 / '1' / true since
 * the flag round-trips through a form-encoded request.
 */
export function isCustomDiscoveryList(record) {
  const flag = record?.isCustom;
  return flag === true || String(flag) === '1';
}

/** Legacy lists: only `version: null`. */
export function isLegacyDiscoveryList(record) {
  return record?.version === null;
}

export function getDiscoveryPlatformNetworks(record) {
  if (!record) return [];
  const { socialNetwork, platforms } = record;

  if (hasSocialNetworkValue(record)) {
    return [String(socialNetwork)];
  }

  let names = platformsFromFilterJson(record);
  if (!names.length && Array.isArray(platforms) && platforms.length) {
    names = platforms;
  }
  return names.map((p) => String(p));
}

function capitalizeEachWord(text) {
  if (text == null || text === '') return '';
  return String(text)
    .trim()
    .split(/\s+/)
    .map((word) => {
      if (!word) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

export function getDiscoveryDisplayName(record) {
  if (!record) return '';
  const { filterJson } = record;
  let raw = '';
  const parsed = parseFilterJson(filterJson);
  if (parsed) {
    try {
      if (record.version === 'V2') {
        raw = parsed.keyword;
      } else {
        raw = parsed.filter?.influencer?.keywords;
      }
    } catch {
      raw = '';
    }
  }

  if (!raw) {
    raw = record.name;
  }

  return capitalizeEachWord(raw);
}

export function getDiscoveryCreatedRaw(record) {
  if (!record) return undefined;
  return record.createdTimestamp ?? record.createdTimeStamp;
}

/**
 * Emoji shown in the list-card / table thumbnail tile. `emoji` is returned by
 * /universal-list/fetchLists (currently derived deterministically from the list
 * id server-side). Falls back to null so the UI can show a neutral glyph.
 */
export function getDiscoveryEmoji(record) {
  if (!record) return null;
  return record.emoji ?? record.icon ?? null;
}

/**
 * Full name of the account that created the discovery. `creatorName` is returned
 * by /universal-list/fetchLists; the other keys are tolerated defensively.
 */
export function getDiscoveryCreatedBy(record) {
  if (!record) return null;
  return (
    record.creatorName ??
    record.createdBy ??
    record.created_by ??
    record.createdByName ??
    record.userName ??
    record.owner ??
    null
  );
}

/**
 * Deterministic soft-pastel background for the emoji tile. Derived from the list
 * id so it is stable per list (never flickers across renders / re-fetches) yet
 * varies between lists. Kept light (high lightness) so any colorful emoji glyph
 * reads with good contrast on top of it.
 */
function hueFromKey(value) {
  const s = String(value ?? '');
  let h = 0;
  // Mod by a prime each step keeps the running value small (no overflow / no
  // bitwise) while still spreading hues well across lists.
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) % 997;
  }
  return h % 360;
}

export function getDiscoveryAvatarColor(record) {
  const key = record?.id ?? record?.hashCode ?? record?.name ?? '';
  // AAPU-6711: Lickly picks from a soft DS brand-tint set (deterministic by key) instead of the
  // full-spectrum pastel; other brands keep the hue-based pastel.
  const isLickly =
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-tenant') === 'lickly';
  if (isLickly) {
    const licklyTints = [
      'var(--brand-tint-50)',
      'var(--ds-champagne-100)',
      'var(--ds-blue-100)',
      'var(--ds-neutral-100)',
    ];
    return licklyTints[hueFromKey(key) % licklyTints.length];
  }
  return `hsl(${hueFromKey(key)}, 60%, 85%)`;
}

export function getDiscoveryLastUpdatedRaw(record) {
  if (!record) return undefined;
  return record.updatedAt ?? record.updatedAt ?? '';
}

function normalizeListRecordStatus(s) {
  const u = String(s || '').toLowerCase();
  if (u === 'completed' || u === 'complete') return 'completed';
  if (u === 'inprogress' || u === 'in_progress' || u === 'pending') return 'inProgress';
  if (u === 'failed' || u === 'error') return 'failed';
  // A cancelled run is terminal; surface it distinctly so the card/table can offer
  // a Rerun action instead of View results.
  if (u === 'cancelled' || u === 'canceled') return 'cancelled';
  return null;
}

export function mapCheckStatusEntryToUiStatus(apiStatus) {
  const u = String(apiStatus || '').toUpperCase();
  if (u === 'COMPLETED' || u === 'COMPLETE') return 'completed';
  if (u === 'ERROR' || u === 'FAILED') return 'failed';
  if (u === 'PENDING' || u === 'IN_PROGRESS') return 'inProgress';
  if (u === 'CANCELLED' || u === 'CANCELED') return 'cancelled';
  return null;
}

export function getDiscoveryStatus(record, checkStatusList) {
  if (!record) return null;

  if (isLegacyDiscoveryList(record)) {
    return normalizeListRecordStatus(record.status);
  }

  const id = String(record.id);
  const entry = (Array.isArray(checkStatusList) ? checkStatusList : []).find(
    (item) => String(item?.universalListId) === id
  );

  if (entry) {
    const mapped = mapCheckStatusEntryToUiStatus(entry.status);
    if (mapped) return mapped;
  }

  return normalizeListRecordStatus(record.status);
}

const FOLLOWER_BUCKETS = {
  nano: { min: 100, max: 10000 },
  micro: { min: 10001, max: 50000 },
  mid: { min: 50001, max: 500000 },
  macro: { min: 500001, max: 1000000 },
  mega: { min: 1000001, max: INF_SIZE_MAX_VALUE },
};

function normalizeNetworkIds(activeNetworks) {
  const arr = Array.isArray(activeNetworks) ? activeNetworks : [activeNetworks];
  return arr.filter(Boolean);
}

function platformsForApi(activeNetworks) {
  return normalizeNetworkIds(activeNetworks).map((id) => {
    const key = String(id).toLowerCase();
    // Registry display name gives consistent brand casing (Instagram/TikTok/YouTube/LinkedIn).
    return SOCIAL_NETWORKS[key]?.name || id;
  });
}

function followersRangeFromBuckets(buckets) {
  if (!Array.isArray(buckets) || buckets.length === 0) return null;
  let min = Infinity;
  let max = -Infinity;
  buckets.forEach((b) => {
    const r = FOLLOWER_BUCKETS[b];
    if (r) {
      min = Math.min(min, r.min);
      max = Math.max(max, r.max);
    }
  });
  if (min === Infinity) return null;
  return { minFollowers: min, maxFollowers: max };
}

function toApiString(value) {
  if (value == null || value === '') return undefined;
  if (Array.isArray(value)) {
    if (value.length === 0) return undefined;
    return value.join(', ');
  }
  return String(value);
}

export function buildUniversalDiscoveryCreateFilters({
  userPrompt,
  activeNetworks,
  advancedFilters = {},
}) {
  const payload = {
    platforms: platformsForApi(activeNetworks),
    keyword: (userPrompt ?? '').trim(),
  };

  const range = followersRangeFromBuckets(advancedFilters.numberOfFollowers);
  if (range) {
    payload.minFollowers = range.minFollowers;
    payload.maxFollowers = range.maxFollowers;
  }

  const location = toApiString(advancedFilters.location);
  if (location) payload.location = location;

  const gender = toApiString(advancedFilters.gender);
  if (gender) payload.gender = gender;

  const ethnicity = toApiString(advancedFilters.ethnicity);
  if (ethnicity) payload.ethnicity = ethnicity;

  const language = toApiString(advancedFilters.language);
  if (language) payload.language = language;

  const age = toApiString(advancedFilters.age);
  if (age) payload.age = age;

  return payload;
}

/**
 * Parse `respData` from universalListCreatorFeedback (object or JSON string).
 * Shape is typically { Instagram: [...], TikTok: [...] }.
 */
export function parseUniversalListCreatorRespData(respData) {
  if (respData == null) return null;
  if (typeof respData === 'string') {
    try {
      return JSON.parse(respData);
    } catch {
      return null;
    }
  }
  if (typeof respData === 'object') return respData;
  return null;
}

function parseUserSelectionMap(rawSelection) {
  if (rawSelection == null) return null;
  if (typeof rawSelection === 'string') {
    try {
      const parsed = JSON.parse(rawSelection);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }
  if (typeof rawSelection === 'object') return rawSelection;
  return null;
}

function normalizeUserSelection(value) {
  if (value === 1 || value === '1' || value === true) return 'like';
  if (value === 0 || value === '0' || value === false) return 'dislike';
  return null;
}

function normalizeUsernameKey(value) {
  if (value == null || value === '') return '';
  return String(value)
    .replace(/^@/, '')
    .toLowerCase();
}

function getUserSelectionByUsername(selectionMap, username) {
  if (!selectionMap || username == null || username === '') return null;
  const candidates = [
    String(username),
    String(username).replace(/^@/, ''),
    String(username).toLowerCase(),
    normalizeUsernameKey(username),
  ];
  const fromCandidates = candidates
    .map((key) => normalizeUserSelection(selectionMap[key]))
    .find(Boolean);
  if (fromCandidates) return fromCandidates;

  const targetNorm = normalizeUsernameKey(username);
  const matchedKey = Object.keys(selectionMap).find((k) => normalizeUsernameKey(k) === targetNorm);
  if (matchedKey != null) {
    return normalizeUserSelection(selectionMap[matchedKey]);
  }
  return null;
}

function getUserSelectionFromMap(selectionMap, { username, creatorReportDetailsId }) {
  if (!selectionMap) return null;
  const byUser = getUserSelectionByUsername(selectionMap, username);
  if (byUser) return byUser;
  if (creatorReportDetailsId != null && creatorReportDetailsId !== '') {
    const idStr = String(creatorReportDetailsId);
    const s = normalizeUserSelection(selectionMap[idStr]);
    if (s) return s;
  }
  return null;
}

/**
 * Prefer the first feedback row that actually carries a userSelection map.
 * Also checks `userSelection` on the row itself (not only inside respData).
 */
function getUserSelectionMapFromFeedback(feedbackRows) {
  if (!Array.isArray(feedbackRows)) return null;
  for (let i = 0; i < feedbackRows.length; i += 1) {
    const row = feedbackRows[i];
    const fromRow = parseUserSelectionMap(row?.userSelection);
    if (fromRow && Object.keys(fromRow).length > 0) return fromRow;
    const parsed = parseUniversalListCreatorRespData(row?.respData);
    const fromParsed = parseUserSelectionMap(parsed?.userSelection);
    if (fromParsed && Object.keys(fromParsed).length > 0) return fromParsed;
  }
  const parsed0 = parseUniversalListCreatorRespData(feedbackRows[0]?.respData);
  return parseUserSelectionMap(parsed0?.userSelection);
}

function applyUserSelectionFromFeedbackToInfluencers(influencers, feedbackRows) {
  if (!Array.isArray(influencers) || influencers.length === 0) {
    return influencers;
  }
  const userSelectionMap = getUserSelectionMapFromFeedback(feedbackRows);
  if (!userSelectionMap || Object.keys(userSelectionMap).length === 0) {
    return influencers;
  }
  return influencers.map((inf) => {
    const selection = getUserSelectionFromMap(userSelectionMap, {
      username: inf.username,
      creatorReportDetailsId: inf.creatorReportDetailsId,
    });
    if (selection === 'like') {
      return { ...inf, isLike: true, isDislike: false };
    }
    if (selection === 'dislike') {
      return { ...inf, isDislike: true, isLike: false };
    }
    return inf;
  });
}

/** When fetchListById returns `influencers` at the top level, merge likes from feedback. */
function withInfluencersUserSelectionMerging(payload) {
  if (
    !payload ||
    !Array.isArray(payload.universalListCreatorFeedback) ||
    !Array.isArray(payload.influencers) ||
    payload.influencers.length === 0
  ) {
    return payload;
  }
  return {
    ...payload,
    influencers: applyUserSelectionFromFeedbackToInfluencers(
      payload.influencers,
      payload.universalListCreatorFeedback
    ),
  };
}

function engagementRatioFromApiRate(rate) {
  if (rate == null) return null;
  return rate > 1 ? rate / 100 : rate;
}

function engagementPercentFromApiRate(rate) {
  if (rate == null) return null;
  return rate > 1 ? rate : rate * 100;
}

/**
 * Flattens creators from universalListCreatorFeedback[].respData into UI influencer rows.
 */
export function flattenInfluencersFromCreatorFeedback(feedbackRows) {
  const rows = [];
  if (!Array.isArray(feedbackRows)) return rows;
  const userSelectionMap = getUserSelectionMapFromFeedback(feedbackRows);

  feedbackRows.forEach((row) => {
    const parsed = parseUniversalListCreatorRespData(row?.respData);
    if (!parsed || typeof parsed !== 'object') return;

    Object.entries(parsed).forEach(([platformKey, creators]) => {
      if (!Array.isArray(creators)) return;
      const socialNetwork = String(platformKey).toLowerCase();

      creators.forEach((c, idx) => {
        const idRaw = c.creatorReportsId ?? c.creatorDiscoveryId;
        const selection = userSelectionMap
          ? getUserSelectionFromMap(userSelectionMap, {
              username: c.username,
              creatorReportDetailsId: idRaw,
            })
          : null;
        const creatorReportDetailsId =
          idRaw != null ? String(idRaw) : `ud-fb-${row.id}-${socialNetwork}-${c.username ?? idx}`;

        const rate = c.average_engagement_rate;
        const ratio = engagementRatioFromApiRate(rate);
        const followers = c.followers ?? null;
        let engagements = c.engagements != null ? c.engagements : null;
        if (engagements == null && followers != null && ratio != null) {
          engagements = Math.round(followers * ratio);
        }
        // Partial (first-wave) records may lack a score; `?? null` does NOT catch
        // NaN, so guard explicitly to avoid a "NaN" string leaking into the UI.
        const scoreNum = Number(c.score);
        const score = Number.isFinite(scoreNum) ? (scoreNum * 100).toFixed(0) : null;

        rows.push({
          creatorReportDetailsId,
          universalListCreatorFeedbackId: row.id != null ? String(row.id) : null,
          username: c.username ?? '',
          fullname: c.name ?? c.username ?? '',
          picture: c.picture ?? c.profile_picture ?? null,
          url: c.url,
          followers,
          engagements,
          engagementRate: engagementPercentFromApiRate(rate),
          score,
          socialNetwork,
          isLike: selection === 'like',
          isDislike: selection === 'dislike',
          interests: Array.isArray(c.interests) ? c.interests : [],
          rationale: c.rationale,
          demographics: c.demographics ?? null,
          badge: c.badge,
        });
      });
    });
  });

  return rows;
}

function discoveryScoreToNumber(score) {
  if (score == null || score === '') return NaN;
  const n = Number(score);
  return Number.isNaN(n) ? NaN : n;
}

export function buildDiscoveryBadgeMap(influencers, allInfluencersForMaxScore = null) {
  const map = new Map();
  if (!Array.isArray(influencers) || influencers.length === 0) {
    return map;
  }

  const maxSource =
    Array.isArray(allInfluencersForMaxScore) && allInfluencersForMaxScore.length > 0
      ? allInfluencersForMaxScore
      : influencers;

  const numericScores = maxSource
    .map((inf) => discoveryScoreToNumber(inf.score))
    .filter((n) => !Number.isNaN(n));

  if (numericScores.length === 0) {
    influencers.forEach((inf) => {
      map.set(String(inf.creatorReportDetailsId), null);
    });
    return map;
  }

  const maxScore = Math.max(...numericScores);

  influencers.forEach((inf) => {
    const id = String(inf.creatorReportDetailsId);
    const s = discoveryScoreToNumber(inf.score);
    if (Number.isNaN(s)) {
      map.set(id, null);
    } else if (s === maxScore) {
      map.set(id, 'Top Scoring');
    } else {
      map.set(id, null);
    }
  });

  return map;
}

/**
 * @typedef {'nano' | 'micro' | 'mid' | 'macro' | 'mega'} FollowerTier
 *
 * @typedef {Object} FollowerTierConfig
 * @property {string} label
 * @property {FollowerTier} tier
 */

/**
 * Single source of truth for influencer follower tier thresholds.
 */
export const FOLLOWER_THRESHOLD = /** @type {const} */ ({
  MEGA: 1_000_000,
  MACRO: 500_000,
  MID: 100_000,
  MICRO: 10_000,
  NANO: 1_000,
});

/**
 * Retrieve follower tier config based on the amoumt of the influencer's followers
 * @param {string|number} followers - Total number of followers for influencer
 * @returns {?FollowerTierConfig}
 */
export function getFollowerSizeTier(followers) {
  const n = Number(followers);
  if (Number.isNaN(n) || followers == null || followers === '') {
    return null;
  }
  if (n >= FOLLOWER_THRESHOLD.MEGA) return { label: 'Mega', tier: 'mega' };
  if (n >= FOLLOWER_THRESHOLD.MACRO) return { label: 'Macro', tier: 'macro' };
  if (n >= FOLLOWER_THRESHOLD.MID) return { label: 'Mid-tier', tier: 'mid' };
  if (n >= FOLLOWER_THRESHOLD.MICRO) return { label: 'Micro', tier: 'micro' };
  if (n >= FOLLOWER_THRESHOLD.NANO) return { label: 'Nano', tier: 'nano' };
  return null;
}

/** UI order for size filter chips (matches `getFollowerSizeTier` keys). */
export const FOLLOWER_SIZE_FILTER_OPTIONS = [
  { tier: 'nano', label: 'Nano' },
  { tier: 'micro', label: 'Micro' },
  { tier: 'mid', label: 'Mid-tier' },
  { tier: 'macro', label: 'Macro' },
  { tier: 'mega', label: 'Mega' },
];

export function createEmptyDiscoveryInfluencerFilters() {
  return {
    sizeTiers: [],
    scoreBuckets: [],
    followersBuckets: [],
    engagementsBuckets: [],
    engagementRateBuckets: [],
  };
}

export const DISCOVERY_SCORE_FILTER_OPTIONS = [
  { id: 'score-0-50', label: '0 – 50' },
  { id: 'score-50-85', label: '50 – 85' },
  { id: 'score-85-100', label: '85 – 100' },
];

export const DISCOVERY_FOLLOWERS_FILTER_OPTIONS = [
  { id: 'fol-0-1k', label: '< 1K' },
  { id: 'fol-1k-10k', label: '1K – 10K' },
  { id: 'fol-10k-100k', label: '10K – 100K' },
  { id: 'fol-100k-500k', label: '100K – 500K' },
  { id: 'fol-500k-1m', label: '500K – 1M' },
  { id: 'fol-1m-plus', label: '1M+' },
];

export const DISCOVERY_ENGAGEMENTS_FILTER_OPTIONS = [
  { id: 'eng-0-1k', label: '< 1K' },
  { id: 'eng-1k-10k', label: '1K – 10K' },
  { id: 'eng-10k-100k', label: '10K – 100K' },
  { id: 'eng-100k-500k', label: '100K – 500K' },
  { id: 'eng-500k-plus', label: '500K+' },
];

export const DISCOVERY_ENGAGEMENT_RATE_FILTER_OPTIONS = [
  { id: 'er-0-1', label: '0 – 1%' },
  { id: 'er-1-3', label: '1 – 3%' },
  { id: 'er-3-5', label: '3 – 5%' },
  { id: 'er-5-10', label: '5 – 10%' },
  { id: 'er-10-plus', label: '10%+' },
];

function inHalfOpenRange(n, min, maxExclusive) {
  return n >= min && n < maxExclusive;
}

function matchesScoreBucket(id, n) {
  switch (id) {
    case 'score-0-50':
      return inHalfOpenRange(n, 0, 50);
    case 'score-50-85':
      return inHalfOpenRange(n, 50, 85);
    case 'score-85-100':
      return n >= 85;
    default:
      return false;
  }
}

function matchesFollowersBucket(id, n) {
  switch (id) {
    case 'fol-0-1k':
      return inHalfOpenRange(n, 0, 1_000);
    case 'fol-1k-10k':
      return inHalfOpenRange(n, 1_000, 10_000);
    case 'fol-10k-100k':
      return inHalfOpenRange(n, 10_000, 100_000);
    case 'fol-100k-500k':
      return inHalfOpenRange(n, 100_000, 500_000);
    case 'fol-500k-1m':
      return inHalfOpenRange(n, 500_000, 1_000_000);
    case 'fol-1m-plus':
      return n >= 1_000_000;
    default:
      return false;
  }
}

function matchesEngagementsBucket(id, n) {
  switch (id) {
    case 'eng-0-1k':
      return inHalfOpenRange(n, 0, 1_000);
    case 'eng-1k-10k':
      return inHalfOpenRange(n, 1_000, 10_000);
    case 'eng-10k-100k':
      return inHalfOpenRange(n, 10_000, 100_000);
    case 'eng-100k-500k':
      return inHalfOpenRange(n, 100_000, 500_000);
    case 'eng-500k-plus':
      return n >= 500_000;
    default:
      return false;
  }
}

function matchesEngagementRateBucket(id, n) {
  switch (id) {
    case 'er-0-1':
      return inHalfOpenRange(n, 0, 1);
    case 'er-1-3':
      return inHalfOpenRange(n, 1, 3);
    case 'er-3-5':
      return inHalfOpenRange(n, 3, 5);
    case 'er-5-10':
      return inHalfOpenRange(n, 5, 10);
    case 'er-10-plus':
      return n >= 10;
    default:
      return false;
  }
}

export function influencerPassesDiscoveryFilters(inf, filters) {
  if (!filters) return true;

  if (filters.sizeTiers?.length) {
    const tier = getFollowerSizeTier(inf.followers)?.tier;
    if (!tier || !filters.sizeTiers.includes(tier)) return false;
  }

  if (filters.scoreBuckets?.length) {
    const n = inf.score == null || inf.score === '' ? NaN : Number(inf.score);
    if (Number.isNaN(n)) return false;
    if (!filters.scoreBuckets.some((id) => matchesScoreBucket(id, n))) {
      return false;
    }
  }

  if (filters.followersBuckets?.length) {
    const n = inf.followers == null || inf.followers === '' ? NaN : Number(inf.followers);
    if (Number.isNaN(n)) return false;
    if (!filters.followersBuckets.some((id) => matchesFollowersBucket(id, n))) {
      return false;
    }
  }

  if (filters.engagementsBuckets?.length) {
    const n = inf.engagements == null || inf.engagements === '' ? NaN : Number(inf.engagements);
    if (Number.isNaN(n)) return false;
    if (!filters.engagementsBuckets.some((id) => matchesEngagementsBucket(id, n))) {
      return false;
    }
  }

  if (filters.engagementRateBuckets?.length) {
    const n =
      inf.engagementRate == null || inf.engagementRate === '' ? NaN : Number(inf.engagementRate);
    if (Number.isNaN(n)) return false;
    if (!filters.engagementRateBuckets.some((id) => matchesEngagementRateBucket(id, n))) {
      return false;
    }
  }

  return true;
}

export function countActiveDiscoveryFilters(filters) {
  if (!filters) return 0;
  return (
    (filters.sizeTiers?.length || 0) +
    (filters.scoreBuckets?.length || 0) +
    (filters.followersBuckets?.length || 0) +
    (filters.engagementsBuckets?.length || 0) +
    (filters.engagementRateBuckets?.length || 0)
  );
}

/**
 * fetchListById may return legacy detail or { universalList, universalListCreatorFeedback }.
 * Produces a single detail object with `influencers`, `filters`, and `chats` for the UI.
 */
export function normalizeUniversalDiscoveryRecordDetail(data) {
  if (!data || typeof data !== 'object') return data;

  let payload = data;
  if (
    payload.universalList == null &&
    payload.data &&
    typeof payload.data === 'object' &&
    payload.data.universalList != null
  ) {
    payload = payload.data;
  }

  const hasV2DetailShape =
    payload.universalList != null && Array.isArray(payload.universalListCreatorFeedback);

  if (hasV2DetailShape) {
    const ul = payload.universalList;
    let filters = {};
    if (ul.filterJson) {
      try {
        filters = ul.filterJson;
      } catch {
        // ignore malformed filterJson
      }
    }
    let chats = [];
    if (Array.isArray(ul.chats)) {
      chats = ul.chats;
    } else if (Array.isArray(payload.chats)) {
      chats = payload.chats;
    }

    return {
      ...ul,
      filters,
      chats,
      influencers: flattenInfluencersFromCreatorFeedback(payload.universalListCreatorFeedback),
      universalListCreatorFeedback: payload.universalListCreatorFeedback,
    };
  }

  if (!payload.filters && payload.filterJson) {
    const filters = parseFilterJson(payload.filterJson) ?? {};
    return withInfluencersUserSelectionMerging({ ...payload, filters });
  }

  return withInfluencersUserSelectionMerging(payload);
}

// --- polling: DiscoveryResults registers restart; index triggers after create ---

let restartPollingImpl = null;

export function setUniversalDiscoveryRestartPolling(fn) {
  restartPollingImpl = typeof fn === 'function' ? fn : null;
}

export function restartUniversalDiscoveryPolling() {
  // eslint-disable-next-line no-unused-expressions
  restartPollingImpl?.();
}
